'use client';

import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useGTM } from '@/features/Analytics';
import { useBooking } from '../../model/store/booking';
import { useBookingModals } from '../../model/store/bookingModals';
import { TimeSlotsUI } from './TimeSlotsUI';
import type { ISchedule } from '@/entities/booking/model/types';
import type { TimeSlotProps } from '../../model/types';
export const TimeSlotsClient: React.FC<TimeSlotProps> = ({
  initialDay,
  bookingData,
  doctorId,
  medcenterId: medcenterIdProp,
  mini = false,
  pageType,
  timeSlotClick
}) => {
  const {
    medcenterId,
    setIinRequired,
    setChangedSlot,
    resetChangedSlot,
    createBooking,
    booking: bookingStore
  } = useBooking();
  const {
    setVisibleBookingModal
  } = useBookingModals();
  const {
    trackEvent
  } = useGTM(pageType);
  const hasLoggedDayRef = useRef(false);
  const hasLoggedSlotRef = useRef(false);
  const [schedule, setSchedule] = useState<ISchedule[]>(bookingData.length && bookingData[0].onlineBooking.schedule ? bookingData[0].onlineBooking.schedule : []);
  const calcInitialDay = useMemo(() => {
    if (medcenterId) {
      const bookingDataItem = bookingData.find(data => data.medcenterId === medcenterId);
      if (bookingDataItem?.onlineBooking?.schedule) {
        const day = bookingDataItem.onlineBooking.schedule.find(day => day.isWork && day.timeslots);
        return day ? {
          ...day,
          timeslots: day.timeslots
        } : null;
      }
    }
    return initialDay ? {
      ...initialDay,
      timeslots: initialDay.timeslots
    } : null;
  }, [medcenterId, bookingData, initialDay]);
  const selectedDay = useMemo(() => {
    const storeBooking = bookingStore.find(item => item.doctorId === doctorId);
    if (storeBooking?.changedSlot) {
      return storeBooking.changedSlot.day;
    }
    return calcInitialDay;
  }, [bookingStore, doctorId, calcInitialDay]);
  const selectedSlot = useMemo(() => {
    const storeBooking = bookingStore.find(item => item.doctorId === doctorId);
    if (storeBooking?.changedSlot) {
      return storeBooking.changedSlot.slot;
    }
    return selectedDay?.timeslots?.[0] || '';
  }, [bookingStore, doctorId, selectedDay]);
  const handleDayClick = (day: ISchedule) => {
    if (!hasLoggedDayRef.current) {
      trackEvent('click_day');
      hasLoggedDayRef.current = true;
    }
    if (day.isWork && day.dayOfMonth !== selectedDay?.dayOfMonth) {
      const newSelectedDay = {
        ...day,
        timeslots: day.timeslots
      };
      setChangedSlot({
        day: newSelectedDay,
        slot: newSelectedDay.timeslots![0],
        id: doctorId
      });
    }
  };
  const handleSlotClick = (slot: string) => {
    if (!hasLoggedSlotRef.current) {
      trackEvent('click_timeslot');
      hasLoggedSlotRef.current = true;
    }
    if (selectedDay) {
      setChangedSlot({
        day: selectedDay,
        slot,
        id: doctorId
      });
    }
    setVisibleBookingModal(true, doctorId);
    if (typeof timeSlotClick === 'function') {
      timeSlotClick(doctorId, bookingData);
    }
  };
  const setSelectedSlot = (slot: string) => {
    if (selectedDay) {
      setChangedSlot({
        day: selectedDay,
        slot,
        id: doctorId
      });
    }
  };
  useEffect(() => {
    const initialSlot = selectedDay?.timeslots?.[0] || '';
    const actualMedcenterId = medcenterId || medcenterIdProp || bookingData[0]?.medcenterId || 0;
    createBooking({
      doctorId,
      medcenterId: actualMedcenterId,
      initialDay: calcInitialDay,
      initialSlot
    });
    if (actualMedcenterId) {
      const bookingDataItem = bookingData.find(data => data.medcenterId === actualMedcenterId);
      if (bookingDataItem) {
        setIinRequired({
          iin: bookingDataItem.onlineBooking?.iinRequired,
          id: doctorId
        });
        const newSchedule = bookingDataItem.onlineBooking?.schedule || null;
        setSchedule(newSchedule ? newSchedule : []);
        if (!newSchedule) {
          resetChangedSlot(actualMedcenterId);
        }
      }
    } else {
      setIinRequired({
        iin: bookingData.length && bookingData[0].onlineBooking ? bookingData[0].onlineBooking.iinRequired : false,
        id: doctorId
      });
    }
    return () => {
      hasLoggedDayRef.current = false;
      hasLoggedSlotRef.current = false;
    };
  }, [bookingData, doctorId, medcenterId, calcInitialDay, selectedDay]);
  useEffect(() => {
    if (medcenterId) {
      const bookingDataItem = bookingData.find(data => data.medcenterId === medcenterId);
      if (bookingDataItem?.onlineBooking?.schedule) {
        const newSchedule = bookingDataItem.onlineBooking.schedule;
        setSchedule(newSchedule);
        const workingDays = newSchedule.filter(day => day.isWork && day.timeslots && day.timeslots.length > 0);
        if (workingDays.length) {
          const firstWorkingDay = workingDays[0];
          const firstAvailableSlot = firstWorkingDay.timeslots![0];
          setChangedSlot({
            day: firstWorkingDay,
            slot: firstAvailableSlot,
            id: doctorId
          });
        }
      }
    }
  }, [bookingData, medcenterId, doctorId, setChangedSlot]);
  return <TimeSlotsUI schedule={schedule} selectedDay={selectedDay} selectedSlot={selectedSlot} onDayClick={handleDayClick} onSlotClick={handleSlotClick} setSelectedSlot={setSelectedSlot} mini={mini} pageType={pageType} data-sentry-element="TimeSlotsUI" data-sentry-component="TimeSlotsClient" data-sentry-source-file="index.tsx" />;
};